// ClientList.tsx
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { useGetClientsQuery } from "@/api/clientApi";
import CreateClient from "./CreateClient";
import { Briefcase, Plus, Search, Edit2 } from "lucide-react";

export default function ClientList() {
  const navigate = useNavigate();
  const { data = [], isLoading, refetch } = useGetClientsQuery();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<"ALL" | "ACTIVE" | "INACTIVE">("ALL");

  const handleClientCreated = () => {
    setDialogOpen(false);
    refetch();
  };

  const filteredClients = data.filter((client: any) => {
    const matchesSearch = client.companyName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         client.contactEmail.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === "ALL" || client.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  return (
    <div className="p-6 space-y-4">
      <div className="flex justify-between items-center bg-white dark:bg-slate-900 p-6 rounded-2xl border border-slate-100 dark:border-slate-800 shadow-sm">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-emerald-50 dark:bg-emerald-900/20 rounded-xl">
             <Briefcase className="w-6 h-6 text-emerald-600" />
          </div>
          <div>
            <h1 className="text-2xl font-black tracking-tight text-slate-900 dark:text-slate-100">Clients</h1>
            <p className="text-slate-500 text-sm font-medium">Manage corporate clients and their diagnostic requirements.</p>
          </div>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl px-5 h-11 font-bold shadow-lg shadow-emerald-100 dark:shadow-none transition-all active:scale-95">
              <Plus className="w-4 h-4 mr-2" />
              Add Client
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl rounded-2xl border-2 p-0 overflow-hidden bg-white dark:bg-slate-900 border-slate-100 dark:border-slate-800">
            <DialogHeader className="p-6 pb-0">
              <DialogTitle className="text-2xl font-black tracking-tight text-slate-900 dark:text-slate-100 uppercase">
                Register New Client
              </DialogTitle>
            </DialogHeader>
            <div className="p-6 max-h-[80vh] overflow-y-auto custom-scrollbar">
              <CreateClient onSuccess={handleClientCreated} onClose={() => setDialogOpen(false)} />
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4 items-center">
        <div className="relative flex-1 w-full group">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 group-focus-within:text-emerald-500 transition-colors" />
          <Input
            placeholder="Search company or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 h-11 border-slate-200 rounded-xl bg-white shadow-sm focus-visible:ring-emerald-500"
          />
        </div>
        <Select
          value={statusFilter}
          onValueChange={(val) => setStatusFilter(val as any)}
        >
          <SelectTrigger className="h-11 w-full md:w-48 rounded-xl border-slate-200 bg-white">
            <SelectValue placeholder="All Status" />
          </SelectTrigger>
          <SelectContent className="rounded-xl">
            <SelectItem value="ALL">All Status</SelectItem>
            <SelectItem value="ACTIVE">Active</SelectItem>
            <SelectItem value="INACTIVE">Inactive</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>All Clients ({filteredClients.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="rounded-xl overflow-hidden border border-slate-100">
            <Table>
              <TableHeader className="bg-slate-50/50">
                <TableRow className="hover:bg-transparent">
                  <TableHead className="font-bold">Company</TableHead>
                  <TableHead className="font-bold">EIN</TableHead>
                  <TableHead className="font-bold">Email</TableHead>
                  <TableHead className="font-bold">Phone</TableHead>
                  <TableHead className="font-bold">City</TableHead>
                  <TableHead className="font-bold text-center">Status</TableHead>
                  <TableHead className="font-bold text-right pr-11">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading
                  ? [...Array(5)].map((_, i) => (
                      <TableRow key={i}>
                        <TableCell colSpan={7} className="p-6">
                          <div className="h-4 w-full bg-slate-100 animate-pulse rounded-lg" />
                        </TableCell>
                      </TableRow>
                    ))
                  : filteredClients.map((client: any) => (
                      <TableRow
                        key={client.id}
                        className="hover:bg-slate-50/50 cursor-pointer transition-colors group"
                        onClick={() => navigate(`/super-admin/clients/${client.id}`)}
                      >
                        <TableCell className="font-black text-slate-900 dark:text-slate-100">
                          {client.companyName}
                        </TableCell>
                        <TableCell className="text-slate-600">{client.ein || "N/A"}</TableCell>
                        <TableCell className="text-slate-600">{client.contactEmail}</TableCell>
                        <TableCell className="text-slate-600">{client.contactPhone || "N/A"}</TableCell>
                        <TableCell className="text-slate-600">{client.city || "N/A"}</TableCell>
                        <TableCell className="text-center">
                          <Badge
                            className={`font-bold border-0 ${
                              client.status === "ACTIVE"
                                ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-100"
                                : "bg-slate-100 text-slate-600 hover:bg-slate-100"
                            }`}
                          >
                            {client.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-9 w-9 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/super-admin/clients/${client.id}?edit=1`);
                              }}
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
